import * as path from 'node:path';
import { gitCreateOrUpdateBranch } from '../../git/branches/create-or-update-branch.js';
import { cliSimpleGit } from '../../git/cli-simple-git.js';
import { resolveRepoPathRelativeToWorkspaceRoot } from '../../git/changes/repo/resolve-repo-path-relative-to-workspace-root.js';
import { forEachWithGitYield } from '../../git/git-runtime.js';
import { isGitRepoDirectory } from '../../git/is-git-repo.js';
import type { LogFn } from '../../log.js';
import type { SessionWorktreeManager } from '../manager/session-worktree-manager.js';
import { captureWorkingTreeWip } from './capture-and-apply-wip.js';
import { resolveIsolatedCheckoutForRepoPathRelativeToWorkspaceRoot } from './resolve-isolated-checkout-for-repo-path-relative-to-workspace-root.js';
import { resolveMainRepoPath } from './resolve-main-repo-path.js';
import type { SessionRepoDeployState } from './types.js';

export async function collectSessionRepoDeployStates(options: {
  sessionWorktreeManager: SessionWorktreeManager;
  sessionId: string;
  deployBranch: string;
  bridgeRoot: string;
  log: LogFn;
  previewParentPath?: string | null;
  previewCheckoutPaths?: string[];
}): Promise<SessionRepoDeployState[]> {
  const {
    sessionWorktreeManager,
    sessionId,
    deployBranch,
    bridgeRoot,
    log,
    previewParentPath = null,
    previewCheckoutPaths = [],
  } = options;
  const sid = sessionId.trim();
  const sessionTargets = await sessionWorktreeManager.resolveSessionRepoCheckoutPaths(sid);
  const sessionParentPath = sessionWorktreeManager.getSessionParentPath(sid);
  const states: SessionRepoDeployState[] = [];

  await forEachWithGitYield(sessionTargets, async (sessionCheckout) => {
    const checkoutPath = path.resolve(sessionCheckout);
    if (!(await isGitRepoDirectory(checkoutPath))) return;

    const repoPathRelativeToWorkspaceRoot = await resolveRepoPathRelativeToWorkspaceRoot({
      checkoutPath,
      bridgeRoot,
      sessionParentPath: sessionParentPath ? path.resolve(sessionParentPath) : null,
    });
    if (!repoPathRelativeToWorkspaceRoot) return;

    const mainRepoPath = resolveMainRepoPath(checkoutPath);
    const headSha = (await cliSimpleGit(checkoutPath).revparse(['HEAD'])).trim();
    const previewCheckout =
      previewCheckoutPaths.length > 0
        ? await resolveIsolatedCheckoutForRepoPathRelativeToWorkspaceRoot({
            checkoutPaths: previewCheckoutPaths,
            sessionParentPath: previewParentPath ? path.resolve(previewParentPath) : null,
            bridgeRoot,
            repoPathRelativeToWorkspaceRoot,
          })
        : null;
    await gitCreateOrUpdateBranch(mainRepoPath, deployBranch, headSha, {
      branchCheckedOutWorktreePath: previewCheckout ?? undefined,
    });
    const wip = await captureWorkingTreeWip(checkoutPath, log);
    states.push({ repoPathRelativeToWorkspaceRoot, sessionCheckout: checkoutPath, wip });
  });

  return states;
}
